import { RefObject, useCallback, useEffect, useState } from "react";
import type { ReactCropperElement } from "react-cropper";

import {
  ASPECT_RATIOS,
  clamp,
  getCurrentZoomRatio,
  getFitZoomRatio,
  MAX_ZOOM,
  maximizeCropBox,
  safeZoomTo
} from "./cropperUtils";

interface UseImageFramingProps {
  cropperRef: RefObject<ReactCropperElement>;
  fileId: string;
  isActive: boolean;
}

export function useImageFraming({ cropperRef, fileId, isActive }: UseImageFramingProps) {
  const [aspectIndex, setAspectIndex] = useState(0);
  const [rotation, setRotation] = useState(0);
  const [zoomMin, setZoomMin] = useState(0.1);
  const [zoomValue, setZoomValue] = useState(0);

  const aspect = ASPECT_RATIOS[aspectIndex];

  const syncZoom = useCallback(() => {
    const cropper = cropperRef.current?.cropper;
    if (!cropper) return;

    const fit = getFitZoomRatio(cropper);
    const current = clamp(getCurrentZoomRatio(cropper), fit, MAX_ZOOM);
    setZoomMin(fit);
    setZoomValue(Math.round(((current - fit) / Math.max(0.01, MAX_ZOOM - fit)) * 100));
  }, [cropperRef]);

  const autoFrame = useCallback(() => {
    const cropper = cropperRef.current?.cropper;
    if (!cropper) return;

    cropper.setAspectRatio(aspect.value);
    const fit = getFitZoomRatio(cropper);
    setZoomMin(fit);
    safeZoomTo(cropper, fit);
    window.setTimeout(() => {
      maximizeCropBox(cropper, aspect.value);
      syncZoom();
    }, 0);
  }, [aspect.value, cropperRef, syncZoom]);

  const reset = useCallback(() => {
    const cropper = cropperRef.current?.cropper;
    if (!cropper) return;
    cropper.reset();
    setRotation(0);
    window.setTimeout(autoFrame, 0);
  }, [autoFrame, cropperRef]);

  const rotate = useCallback(() => {
    const cropper = cropperRef.current?.cropper;
    if (!cropper) return;

    const nextRotation = (rotation + 90) % 360;
    cropper.rotateTo(nextRotation);
    setRotation(nextRotation);
    window.setTimeout(autoFrame, 0);
  }, [autoFrame, cropperRef, rotation]);

  const cycleAspectRatio = useCallback(() => {
    const nextIndex = (aspectIndex + 1) % ASPECT_RATIOS.length;
    const nextAspect = ASPECT_RATIOS[nextIndex];
    setAspectIndex(nextIndex);

    const cropper = cropperRef.current?.cropper;
    if (!cropper) return;
    cropper.setAspectRatio(nextAspect.value);
    window.setTimeout(() => maximizeCropBox(cropper, nextAspect.value), 0);
  }, [aspectIndex, cropperRef]);

  const changeZoom = useCallback(
    (value: number) => {
      setZoomValue(value);
      const ratio = zoomMin + (value / 100) * (MAX_ZOOM - zoomMin);
      safeZoomTo(cropperRef.current?.cropper, clamp(ratio, zoomMin, MAX_ZOOM));
    },
    [cropperRef, zoomMin]
  );

  useEffect(() => {
    if (!isActive) return;
    setAspectIndex(0);
    setRotation(0);
    setZoomValue(0);
  }, [fileId, isActive]);

  useEffect(() => {
    if (!isActive) return;

    const handleResize = () => window.setTimeout(autoFrame, 0);
    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
    };
  }, [autoFrame, isActive]);

  return {
    aspectLabel: aspect.label,
    rotation,
    zoomValue,
    autoFrame,
    reset,
    rotate,
    cycleAspectRatio,
    changeZoom,
    syncZoom
  };
}
